import { patchState, signalStore, withComputed, withMethods, withProps, withState } from "@ngrx/signals";
import { inject } from "@angular/core";
import { PricingStore } from "../s25.event.pricing.store";
import { FlsStore } from "../../../../stores/fls.store";
import { s25Resource } from "../../../../util/s25.resource";
import { S25Util } from "../../../../util/s25-util";
import { S25Const } from "../../../../util/s25-const";
import { AccessLevels } from "../../../../pojo/Fls";
import { ICheckoutRequest, IPaymentDetails, PaymentService } from "../../s25-payments/payment.service";
import { IntegrationService, IntegrationTypes } from "../../../integrations/integration.service";
import { FreshbooksService } from "../../../../services/freshbooks.service";
import { EventService } from "../../../../services/event.service";
import { PreferenceService } from "../../../../services/preference.service";
import { ContactService } from "../../../../services/contact.service";
import { OrganizationService } from "../../../../services/organization.service";
import { Report } from "../../../../pojo/Report";
import { FBInvoiceData } from "../../../../pojo/Pricing";
import { InvoiceTotals } from "../invoice/totals/invoice-totals";
import { TelemetryService } from "../../../../services/telemetry.service";
import { BpeService, EmailTemplate } from "../../../bpe/bpe.service";
import { FormAssignmentRecipient } from "../../../data-ingestion/data.ingestion.service";
import { PaymentEmailTemplate } from "./payment-email-template";
export type PaymentSourceId = IntegrationTypes.stripe | IntegrationTypes.sevenPoint | "manual";
export type PaymentSource = { id: PaymentSourceId; name: string };

export const MANUAL_SOURCE: PaymentSource = { id: "manual", name: "Manual" };

function createPaymentError(error: unknown, type: string): string {
    if ((error as { status?: number })?.status !== 409) {
        return S25Util.errorText(error, S25Const.defaultErrorMessage);
    }
    if (type === "FINAL") return "A BALANCE payment already exists. Only one payment of this type is allowed.";
    return `An error occurred while creating the ${type} payment. Please try again.`;
}

export type PaymentRequestMode = "payment" | "email";

type State = {
    /** The org whose payment request sidebar is open, if any. */
    requestOrgId: number | undefined;
    /** The logged payment the open sidebar is chasing, when it was opened as a reminder. */
    requestPaymentId: number | undefined;
    requestMode: PaymentRequestMode;
    requestContactId: number | undefined;
    /** The org whose inline "record payment" draft row is showing, if any. */
    draftOrgId: number | undefined;
    composeEventName: string | undefined;
};

const REQUEST_CLOSED: Omit<State, "draftOrgId" | "composeEventName"> = {
    requestOrgId: undefined,
    requestPaymentId: undefined,
    requestMode: "payment",
    requestContactId: undefined,
};

export const PaymentsStore = signalStore(
    withState<State>({ ...REQUEST_CLOSED, draftOrgId: undefined, composeEventName: undefined }),
    withProps(() => ({
        _pricingStore: inject(PricingStore),
        _flsStore: inject(FlsStore),
    })),
    withProps((store) => ({
        _stripeEnabled: s25Resource({
            loader: () => IntegrationService.getIntegrationEnabled(IntegrationTypes.stripe),
        }),
        _sevenPointEnabled: s25Resource({
            loader: () => IntegrationService.getIntegrationEnabled(IntegrationTypes.sevenPoint),
        }),
        _hasFreshbooks: s25Resource({
            if: () => !store._pricingStore.composeOnly(),
            loader: () => FreshbooksService.hasFreshbooksApiToken(),
        }),
        _fbInvoices: s25Resource({
            if: () => !!store._pricingStore.eventId() && !store._pricingStore.composeOnly(),
            params: () => ({ eventId: store._pricingStore.eventId()!, evBillId: store._pricingStore.evBillId() }),
            loader: async ({ params }): Promise<FBInvoiceData | undefined> => {
                const response = await FreshbooksService.getS25Invoices(params.eventId, params.evBillId!);
                return response?.invoice;
            },
        }),
        /**
         * Every report the event can produce, not just its invoices - a payment template may ask to
         * attach a confirmation next to the invoice. Invoice and confirmation are already first.
         */
        _reports: s25Resource({
            if: () => !!store._pricingStore.eventId(),
            params: () => store._pricingStore.eventId()!,
            loader: ({ params: eventId }) => EventService.getEventReports(eventId),
        }),
        // Both are only ever read by the request form, and the event data behind them is expensive,
        // so neither is fetched until a request sidebar is actually open.
        _templates: s25Resource({
            if: () => !!store.requestOrgId() && store._flsStore.hasAtLeast("SYS_TEMPLATE", AccessLevels.Read),
            loader: (): Promise<EmailTemplate[]> => BpeService.getManualTemplates(),
        }),
        /**
         * Supplies the form's segments and the addresses behind a template's role names. Neither
         * reads a pricing token, so it skips the legacy billing fetch that carries them - the
         * request form interpolates a chosen template through its own call, which keeps it.
         */
        _bpeEventData: s25Resource({
            if: () => !!store.requestOrgId() && !!store._pricingStore.eventId(),
            params: () => store._pricingStore.eventId()!,
            loader: ({ params: eventId }) =>
                BpeService.getEventData(eventId, { cache: true, longCache: true, skipBilling: true }),
        }),
        _eventName: s25Resource({
            if: () => !!store._pricingStore.eventId() && !store._pricingStore.composeOnly(),
            params: () => store._pricingStore.eventId()!,
            loader: ({ params: eventId }) => EventService.getEventName(eventId),
        }),
        _defaultFrom: s25Resource({
            loader: async () => {
                const [globalFrom, userEmail] = await Promise.all([
                    PreferenceService.getPreferences(["EmailFromAddress"], "S"),
                    ContactService.getCurrentEmail(),
                ]);
                return globalFrom?.EmailFromAddress?.value || userEmail || "";
            },
        }),
    })),
    withComputed((store) => ({
        paymentsByOrg: (): ReadonlyMap<number, IPaymentDetails[]> => {
            const scope = {
                combineRelated: store._pricingStore.combineRelated(),
                eventId: store._pricingStore.eventId(),
            };
            const byOrg = new Map<number, IPaymentDetails[]>();

            for (const payment of store._pricingStore.selectedInvoice()?.payments ?? []) {
                const orgId = payment.organizationId;
                if (!orgId || !InvoiceTotals.isPaymentInScope(payment, scope)) continue;
                byOrg.set(orgId, [...(byOrg.get(orgId) ?? []), payment]);
            }
            return byOrg;
        },
        requestLoading: () => !!store.requestOrgId() && !store._pricingStore.selectedInvoice(),
        emailOnly: () => store.requestMode() === "email",
        viewOnly: () => !store._flsStore.hasFull("MANAGE_PAY"),
        canSendRequest: () => store._flsStore.hasAtLeast("MANAGE_PAY", AccessLevels.Read),
        _paymentsPermitted: () =>
            store._flsStore.hasFull("EVENT_BILLING") && store._flsStore.hasAtLeast("MANAGE_PAY", AccessLevels.Read),
        fbInvoiceData: () => store._fbInvoices.value(),
        fbInvoicesLoading: () => store._fbInvoices.isLoading(),
        defaultFromAddress: () => store._defaultFrom.value() ?? "",
        requestDefaultsLoading: () =>
            store._defaultFrom.isLoading() ||
            store._eventName.isLoading() ||
            store._reports.isLoading() ||
            store._templates.isLoading() ||
            store._bpeEventData.isLoading(),
        /** Manual BPE templates, empty for a user who may not see them. */
        templates: (): EmailTemplate[] => store._templates.value() ?? [],
        profiles: (): PaymentEmailTemplate.EventProfile[] =>
            S25Util.array.forceArray(store._bpeEventData.value()?.profile),
        roleRecipients: (): ReadonlyMap<string, FormAssignmentRecipient> =>
            PaymentEmailTemplate.roleRecipientsByName(store._bpeEventData.value()),
        dateFormat: () => store._pricingStore.dateFormats()?.date,
        eventName: () => store.composeEventName() || store._eventName.value() || "",
        orgNameById: () => store._pricingStore.orgNameById(),
        eventId: () => store._pricingStore.eventId(),
        evBillId: () => store._pricingStore.evBillId(),
    })),
    withComputed((store) => ({
        /** Charge, payments and outstanding balance for every org the invoice touches. */
        figuresByOrg: (): ReadonlyMap<number, InvoiceTotals.OrgFigures> => {
            const invoice = store._pricingStore.selectedInvoice();
            const orgIds = new Set<number>([
                ...(invoice?.billing?.subtotals?.[0]?.account ?? [])
                    .map((account) => account.chargeToId)
                    .filter(Boolean),
                ...store.paymentsByOrg().keys(),
            ]);

            return InvoiceTotals.orgFiguresByOrg(invoice, orgIds, {
                combineRelated: store._pricingStore.combineRelated(),
                eventId: store._pricingStore.eventId(),
            });
        },
        sources: (): PaymentSource[] => {
            if (store.viewOnly()) return [MANUAL_SOURCE];
            return [
                MANUAL_SOURCE,
                ...(store._stripeEnabled.value()
                    ? [{ id: IntegrationTypes.stripe, name: "Stripe" } as PaymentSource]
                    : []),
                ...(store._sevenPointEnabled.value()
                    ? [{ id: IntegrationTypes.sevenPoint, name: "7 Point Solutions" } as PaymentSource]
                    : []),
            ];
        },
        /**
         * The logged payment a reminder is chasing. Looked up rather than held, so it stays current
         * across the invoice reload every payment mutation triggers.
         */
        reminderPayment: (): IPaymentDetails | undefined => {
            const orgId = store.requestOrgId(),
                paymentDetailId = store.requestPaymentId();
            if (!orgId || !paymentDetailId) return undefined;
            return (store.paymentsByOrg().get(orgId) ?? []).find(
                (payment) => payment.paymentDetailId === paymentDetailId,
            );
        },
        eventReports: (): Report.SimpleObject[] => store._reports.value() ?? [],
        reports: (): Report.SimpleObject[] => PaymentEmailTemplate.attachableReports(store._reports.value() ?? []),
        eventHasPayments: () => store._pricingStore.listedInvoices().some((invoice) => !!invoice.payments?.length),
        hasFbInvoices: () => {
            const invoices = store._fbInvoices.value();
            return !!(
                invoices?.setInvoices?.length ||
                invoices?.eventInvoices?.length ||
                invoices?.customBillInvoices?.length
            );
        },
    })),
    withComputed((store) => ({
        /** The payment invoice is attached unless the author says otherwise. */
        defaultReportIds: (): number[] => {
            const invoice = store.reports().find((report) => report.rpt_use === Report.Use.Payment);
            return invoice ? [Number(invoice.rpt_id)] : [];
        },
        /**
         * An institution gets payments or FreshBooks, never both - except where it already had both
         * before the two were made exclusive, which keeps working.
         */
        _legacyBoth: () => store.eventHasPayments() && store.hasFbInvoices(),
    })),
    withComputed((store) => ({
        showPayments: () => store._paymentsPermitted() && (store._legacyBoth() || !store.hasFbInvoices()),
        _freshbooksAvailable: () =>
            !!store._hasFreshbooks.value() &&
            store._pricingStore.canEditPricing() &&
            (store._legacyBoth() || !store.eventHasPayments()),
    })),
    withComputed((store) => ({
        /**
         * FreshBooks only invoices an org that is actually being charged, so an org with no charges
         * on this invoice does not get the section.
         */
        freshbooksOrgIds: (): ReadonlySet<number> => {
            if (!store._freshbooksAvailable()) return new Set();
            return new Set(
                [...store.figuresByOrg()].filter(([, figures]) => figures.charge > 0).map(([orgId]) => orgId),
            );
        },
    })),
    withMethods((store) => {
        const eventId = () => store._pricingStore.eventId();
        const evBillId = () => store._pricingStore.evBillId();

        /**
         * Payments change what an org still owes, so the invoice is refetched rather than patched -
         * subtotals and outstanding balances come from the server alongside the payments themselves.
         */
        const reloadAfterMutation = () => store._pricingStore.reloadInvoices();

        return {
            openRequest(orgId: number) {
                TelemetryService.sendWithSub("Pricing", "Event", "PaymentRequest");
                patchState(store, { ...REQUEST_CLOSED, requestOrgId: orgId });
            },
            /** Chases a payment that already exists, so no new one is logged when the email goes. */
            openReminder(orgId: number, paymentDetailId: number) {
                TelemetryService.sendWithSub("Pricing", "Event", "PaymentReminder");
                patchState(store, { ...REQUEST_CLOSED, requestOrgId: orgId, requestPaymentId: paymentDetailId });
            },
            openContactEmail(orgId: number, contactId: number) {
                TelemetryService.sendWithSub("Pricing", "Org", "ContactEmail");
                patchState(store, {
                    ...REQUEST_CLOSED,
                    requestOrgId: orgId,
                    requestMode: "email",
                    requestContactId: contactId,
                });
            },
            closeRequest() {
                patchState(store, REQUEST_CLOSED);
            },
            setEventName(composeEventName: string | undefined) {
                patchState(store, { composeEventName });
            },
            openDraft(orgId: number) {
                patchState(store, { draftOrgId: orgId });
            },
            closeDraft() {
                patchState(store, { draftOrgId: undefined });
            },
            remainingBalance(orgId: number): number {
                return store.figuresByOrg().get(orgId)?.balance ?? 0;
            },
            /** Every live payment of a type, so the request form can warn about a duplicate. */
            existingPaymentsOfType(orgId: number, type: IPaymentDetails["type"]): IPaymentDetails[] {
                return (store.paymentsByOrg().get(orgId) ?? []).filter(
                    (payment) => payment.type === type && !payment.isVoid,
                );
            },
            async createPayment(
                payload: Omit<ICheckoutRequest, "evBillId" | "eventId" | "currency" | "successUrl" | "cancelUrl"> & {
                    successUrl?: string;
                    cancelUrl?: string;
                },
            ): Promise<{ payment?: IPaymentDetails; error?: string }> {
                const billId = evBillId(),
                    currentEventId = eventId();
                if (!billId || !currentEventId) return { error: S25Const.defaultErrorMessage };

                TelemetryService.sendWithSub(
                    "Pricing",
                    "Event",
                    "PaymentAdd" + S25Util.firstCharToUpper(payload.source),
                );

                const [payment, error] = await S25Util.Maybe(
                    PaymentService.createPayment({
                        currency: "usd",
                        successUrl: "",
                        cancelUrl: "",
                        ...payload,
                        evBillId: billId,
                        eventId: currentEventId,
                    }),
                );

                if (error) return { error: createPaymentError(error, payload.type) };

                await reloadAfterMutation();
                return { payment };
            },
            async voidPayment(paymentDetailId: number, reason: string): Promise<{ error?: string }> {
                TelemetryService.sendWithSub("Pricing", "Event", "PaymentVoid");
                // The service reports its own failures and resolves with null rather than rejecting.
                const [result, error] = await S25Util.Maybe(PaymentService.voidPayment(paymentDetailId, reason));
                if (error || result === null) {
                    return { error: S25Util.errorText(error, "The payment could not be voided.") };
                }

                await reloadAfterMutation();
                return {};
            },
            async markPaid(paymentDetailId: number): Promise<{ error?: string }> {
                const [, error] = await S25Util.Maybe(PaymentService.setPaid(paymentDetailId));
                if (error) return { error: S25Util.errorText(error, S25Const.defaultErrorMessage) };

                await reloadAfterMutation();
                return {};
            },
            async setNotes(paymentDetailId: number, notes: string): Promise<{ error?: string }> {
                const [, error] = await S25Util.Maybe(PaymentService.setPaymentNotes(paymentDetailId, notes));
                if (error) return { error: S25Util.errorText(error, S25Const.defaultErrorMessage) };

                await reloadAfterMutation();
                return {};
            },
            /** FreshBooks owns its own invoice list, so exporting one has to refetch it. */
            reloadFreshbooks() {
                return store._fbInvoices.reloadAndAwait();
            },
            /** Billing contacts head the request email's recipient list. */
            async organizationContacts(orgId: number) {
                const [contacts] = await S25Util.Maybe(OrganizationService.getContactsForOrgs([orgId]));
                return contacts ?? [];
            },
        };
    }),
);

export type PaymentsStore = InstanceType<typeof PaymentsStore>;
